import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { wsService, MessageTypes } from '../services/websocket';
import { useAuth } from '../context/AuthContext';
import DocumentUpload from '../components/DocumentUpload';
import TranslationProgress from '../components/TranslationProgress';
import ChatInterface from '../components/ChatInterface';
import DiffViewer from '../components/DiffViewer';
import ConfidenceScore from '../components/ConfidenceScore';
import PricePanel from '../components/PricePanel';
import ErrorModal from '../components/ErrorModal';
import ScanSuggestionModal, { SCAN_SUGGESTION_CODES } from '../components/ScanSuggestionModal';
import InDesignFontsDialog from '../components/InDesignFontsDialog';
// import OverlayEditor from '../components/OverlayEditor';
import { ArrowLeft, FileText, Eye, ShieldOff, Shield } from 'lucide-react';
import { getLanguageDisplay } from '../constants/languages';
import { DOWNLOAD_FORMATS, downloadedExtension, translatedFilename } from '../constants/documents';
import { countChars, countWords } from '../utils/charCount';
import './ChatPage.css';

function ChatPage() {
  const { t, i18n } = useTranslation('chat');
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [chat, setChat] = useState(null);
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const [progress, setProgress] = useState(null);
  // The fonts and notes of the InDesign file just downloaded, shown once it is saved.
  const [indesignReport, setIndesignReport] = useState(null);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'diff'
  const [diffData, setDiffData] = useState(null);
  const [errorModal, setErrorModal] = useState({ isVisible: false, message: null, code: null });
  const [scanSuggestion, setScanSuggestion] = useState(null);
  // const [showOverlayEditor, setShowOverlayEditor] = useState(false);
  const [translationDuration, setTranslationDuration] = useState(null);
  const [reconnecting, setReconnecting] = useState(false);
  // "NEW" badge on the Compare tab: hides forever after the user opens the
  // tab once. Persisted per-browser via localStorage.
  const [showCompareNewBadge, setShowCompareNewBadge] = useState(() => {
    try { return !localStorage.getItem('cc_seen_compare'); } catch { return false; }
  });
  const translationStartRef = useRef(null);

  // Load chat data
  useEffect(() => {
    setActiveTab('chat');
    setDiffData(null);
    loadChat();
  }, [chatId]);

  // Restore processing state on page load (for reload during translation)
  useEffect(() => {
    if (chat?.processing_state?.stage && chat.processing_state.stage !== 'idle') {
      setTranslating(true);
      setProgress({
        stage: chat.processing_state.stage,
        iteration: chat.processing_state.iteration || 1,
        max_iterations: chat.processing_state.max_iterations || 3,
        message: ''
      });
    }
  }, [chat?.processing_state]);

  // Connect WebSocket
  useEffect(() => {
    if (!chatId || !token) return;

    let cancelled = false;

    const connect = async () => {
      try {
        if (!cancelled) {
          await wsService.connect(chatId, token);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('WebSocket connection failed:', error);
        }
      }
    };

    connect();

    // Set up listeners
    const unsubProgress = wsService.on(MessageTypes.PROGRESS, handleProgress);
    const unsubComplete = wsService.on(MessageTypes.TRANSLATION_COMPLETE, handleTranslationComplete);
    const unsubError = wsService.on(MessageTypes.ERROR, handleError);
    const unsubReconnecting = wsService.on('reconnecting', () => setReconnecting(true));
    const unsubReconnected = wsService.on('reconnected', async () => {
      setReconnecting(false);
      // Check if translation completed while we were disconnected
      if (translating) {
        try {
          const chatData = await api.getChat(chatId);
          const isStillProcessing = chatData?.processing_state?.stage && chatData.processing_state.stage !== 'idle';
          if (!isStillProcessing && chatData.document_id) {
            setTranslating(false);
            setProgress(null);
            setChat(chatData);
            const docData = await api.getDocument(chatData.document_id);
            setDocument(docData);
          }
        } catch (e) {
          console.error('Failed to check completion on reconnect:', e);
        }
      }
    });

    return () => {
      cancelled = true;
      unsubProgress();
      unsubComplete();
      unsubError();
      unsubReconnecting();
      unsubReconnected();
      wsService.disconnect();
    };
  }, [chatId, token]);

  // Followed until it settles, or the button stays disabled until a reload.
  const docxRebuilding = document?.metadata?.docx_rebuild === 'running';
  // Used only by the 1:1 copy editor, off with it.
  // const refreshDocument = useCallback(async () => {
  //   if (!document?.id) return;
  //   setDocument(await api.getDocument(document.id));
  // }, [document?.id]);

  useEffect(() => {
    if (!docxRebuilding || !document?.id) return undefined;
    const POLL_INTERVAL_MS = 4000;
    let cancelled = false;
    const documentId = document.id;
    const intervalId = setInterval(async () => {
      try {
        const fresh = await api.getDocument(documentId);
        if (!cancelled) setDocument(fresh);
      } catch (e) {
        console.error('Word rebuild poll failed:', e);
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [docxRebuilding, document?.id]);

  const loadChat = async () => {
    try {
      const chatData = await api.getChat(chatId);
      setChat(chatData);

      if (chatData.document_id) {
        const docData = await api.getDocument(chatData.document_id);
        setDocument(docData);
        // If the document previously failed, re-show the error modal so
        // the user sees WHY after a page reload - before this, the reason
        // was WS-only and users retried the same broken file 3-8 times.
        // Backend now persists error_code + error_message on the doc.
        if (docData.status === 'failed' && docData.error_code === 'CHAR_LIMIT_REACHED') {
          // Running out of characters is a paywall, not a crash - show the top-up modal.
          window.dispatchEvent(new CustomEvent('char-limit-reached', { detail: docData.error_params }));
        } else if (docData.status === 'failed' && (docData.error_message || docData.error_code)) {
          setErrorModal({
            isVisible: true,
            message: docData.error_message || null,
            code: docData.error_code || null,
          });
        }
      }
    } catch (error) {
      console.error('Failed to load chat:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Defensive polling: while a translation is in flight, periodically check
  // the chat status from the API. If the backend says the task has finished
  // but we never received the WS `translation_complete` event (e.g. Taskiq
  // result backend hiccup, dropped frame, etc.), recover the UI state
  // without forcing the user to reload the page.
  useEffect(() => {
    if (!translating || !chatId) return undefined;

    let cancelled = false;
    const POLL_INTERVAL_MS = 5000;

    const checkStatus = async () => {
      if (cancelled) return;
      try {
        const chatData = await api.getChat(chatId);
        if (cancelled) return;
        const stillProcessing = chatData?.processing_state?.stage && chatData.processing_state.stage !== 'idle';
        if (stillProcessing) return;

        if (chatData.status === 'completed' && chatData.document_id) {
          if (translationStartRef.current) {
            setTranslationDuration(Math.floor((Date.now() - translationStartRef.current) / 1000));
          }
          setTranslating(false);
          setProgress(null);
          setChat(chatData);
          const docData = await api.getDocument(chatData.document_id);
          if (!cancelled) setDocument(docData);
        } else if (chatData.status === 'failed') {
          setTranslating(false);
          setProgress(null);
          setChat(chatData);
        }
      } catch (e) {
        console.error('Translation status poll failed:', e);
      }
    };

    const intervalId = setInterval(checkStatus, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [translating, chatId]);

  const handleProgress = useCallback((data) => {
    setProgress(data);
    setTranslating(true);
  }, []);

  const handleTranslationComplete = useCallback(async (data) => {
    if (translationStartRef.current) {
      setTranslationDuration(Math.floor((Date.now() - translationStartRef.current) / 1000));
    }
    setTranslating(false);
    setProgress(null);

    await loadChat();
  }, [chatId]);

  const handleError = useCallback((data) => {
    setTranslating(false);
    setProgress(null);
    // The failed document as stored now: whether it may be tried again is the server's to say. Read through the
    // chat - the socket's listeners keep this handler from the page's first render.
    api.getChat(chatId)
      .then((current) => (current.document_id ? api.getDocument(current.document_id) : null))
      .then((current) => current && setDocument(current))
      .catch((e) => console.error('Failed to reload the failed document:', e));

    if (data.code === 'CHAR_LIMIT_REACHED') {
      window.dispatchEvent(new CustomEvent('char-limit-reached', {
        detail: data.params
      }));
      return;
    }
    if (SCAN_SUGGESTION_CODES.includes(data.code)) {
      setScanSuggestion(data.code);
      return;
    }
    if (data.code === 'DOCUMENT_LIMIT_REACHED') {
      alert(t('errors.documentLimitReached'));
      return;
    }
    if (data.code === 'INVALID_FILE_TYPE') {
      alert(t('errors.invalidFileType'));
      return;
    }

    // Show error modal for translation failures
    setErrorModal({
      isVisible: true,
      message: data.message || null,
      code: data.code || null
    });
  }, [t, chatId]);

  const startTranslationTimer = () => {
    translationStartRef.current = Date.now();
    setTranslationDuration(null);
  };

  const handleUploadStart = () => {
    startTranslationTimer();
    setTranslating(true);
    setProgress({
      stage: 'preparing',
      iteration: 1,
      max_iterations: 3,
      message: ''
    });
  };

  const handleRetry = async () => {
    startTranslationTimer();
    setTranslating(true);
    setProgress({ stage: 'preparing', iteration: 0, max_iterations: 3, message: '' });
    try {
      await api.retryDocument(document.id);
      setDocument((current) => ({ ...current, status: 'processing', can_retry: false }));
    } catch (error) {
      console.error('Failed to try the document again:', error);
      const detail = error.response?.data?.detail;
      setTranslating(false);
      setProgress(null);
      if (detail?.error_code !== 'CHAR_LIMIT_REACHED') {
        setErrorModal({ isVisible: true, message: detail?.message || null, code: detail?.error_code || null });
      }
    }
  };

  const handleUpload = async (file, languageTo, languageVariant, context) => {
    try {
      await api.uploadDocument(file, chatId, languageTo, languageVariant, context);
    } catch (error) {
      console.error('Failed to upload document:', error);
      const detail = error.response?.data?.detail;
      setTranslating(false);
      setProgress(null);
      if (detail?.error_code === 'FILE_TOO_LARGE') {
        alert(t('errors.fileTooLarge', { maxMb: detail.params?.max_mb }));
      } else if (detail?.error_code === 'PACKAGE_TOO_LARGE') {
        alert(t('errors.packageTooLarge'));
      } else if (detail?.error_code === 'CHAR_LIMIT_REACHED') {
        // Already handled by global event in api.js
      } else if (SCAN_SUGGESTION_CODES.includes(detail?.error_code)) {
        setScanSuggestion(detail.error_code);
      } else {
        setErrorModal({
          isVisible: true,
          message: detail?.message || null,
          code: detail?.error_code || null,
        });
      }
    }
  };

  const handleSendMessage = (message) => {
    // Add user message to UI immediately
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    setChat(prev => ({
      ...prev,
      messages: [...(prev?.messages || []), userMessage]
    }));

    startTranslationTimer();
    setTranslating(true);
    setProgress({
      stage: 'preparing',
      iteration: 1,
      max_iterations: 3,
      message: ''
    });
    wsService.sendMessage(message);
  };

  const handleRegenerate = () => {
    startTranslationTimer();
    setTranslating(true);
    setProgress({
      stage: 'preparing',
      iteration: 1,
      max_iterations: 3,
      message: ''
    });
    wsService.regenerate();
  };

  // Rejects when the download fails: the clicked button says why.
  const handleDownload = async (fileType, fileFormat) => {
    if (!document) return;

    const blob = await api.downloadDocument(document.id, fileType, i18n.resolvedLanguage, fileFormat);
    // The `a.download` attribute overrides the server's Content-Disposition in browsers, so the name is built here.
    const baseName = document.original_filename.replace(/\.[^.]+$/, '');
    let filename;
    if (fileType === 'scan_source') {
      // Raw upload - preserve the client's original extension (.pdf, .png, etc.)
      // so the browser downloads exactly what was uploaded.
      const origExt = document.original_filename.match(/\.[^.]+$/)?.[0] || '.pdf';
      filename = `${baseName}${origExt}`;
    } else if (fileType === 'overlay') {
      // Faithful 1:1 copy - translation painted onto the original scan, always a PDF.
      filename = `${baseName}_overlay.pdf`;
    } else if (fileType === 'before_after') {
      filename = `${baseName}_before_after.jpg`;
    } else if (fileType === 'original') {
      filename = `${baseName}.${downloadedExtension(fileFormat, blob.type)}`;
    } else {
      filename = translatedFilename(
        document.language_from, document.language_to, baseName, downloadedExtension(fileFormat, blob.type),
      );
    }
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = filename;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    URL.revokeObjectURL(url);
    if (fileFormat === DOWNLOAD_FORMATS.idml) {
      // The file is saved: a report that fails must not read as a failed download, to be asked for again.
      try {
        setIndesignReport(await api.getInDesignReport(document.id, fileType));
      } catch (error) {
        console.error('InDesign report failed:', error);
        alert(t('download.indesignReportUnavailable'));
      }
    }
  };

  const loadDiff = async () => {
    if (!document) return;

    if (showCompareNewBadge) {
      try { localStorage.setItem('cc_seen_compare', '1'); } catch { /* private mode - ok */ }
      setShowCompareNewBadge(false);
    }

    try {
      const data = await api.getDocumentDiff(document.id);
      setDiffData(data);
      setActiveTab('diff');
    } catch (error) {
      console.error('Failed to load diff:', error);
    }
  };

  const getLastTranslation = () => {
    if (!chat?.messages) return null;
    const assistantMessages = chat.messages.filter(m => m.role === 'assistant');
    return assistantMessages[assistantMessages.length - 1];
  };

  const lastTranslation = getLastTranslation();
  const hasAnalysis = !!lastTranslation;

  // Source count comes from the backend (the characters read from the source;
  // a sparse scan is charged its per-page minimum instead); the translated
  // count is measured from the rendered translation.
  const sourceChars = document?.metadata?.char_count || null;
  const sourceWords = document?.metadata?.word_count || null;
  const targetChars = useMemo(
    () => (lastTranslation ? countChars(lastTranslation.content) : null),
    [lastTranslation]
  );
  const targetWords = useMemo(
    () => (lastTranslation ? countWords(lastTranslation.content) : null),
    [lastTranslation]
  );

  // Compare tab is only worth surfacing as "NEW" when there's actually
  // something to look at - annotations to align against, or low-confidence
  // segments that warrant review. Otherwise the badge would be noise.
  const lineConfidence = lastTranslation?.metadata?.line_confidence || {};
  const lowConfidenceCount = Object.values(lineConfidence).filter((c) => c < 0.85).length;
  const annotationsCount = lastTranslation?.metadata?.annotations?.length || 0;
  const hasInterestingDiff = annotationsCount > 0 || lowConfidenceCount > 0;

  // A scan only recognised has no translation to compare, price or explain.
  const recognitionOnly = document?.service === 'recognition';

  // Faithful 1:1 overlay PDF: only offered for completed scanned docs. Enabled
  // only when the doc was actually translated in overlay mode and the overlay
  // wasn't degraded; otherwise shown greyed with an explanatory tooltip.
  // The 1:1 copy is off while scans are built by the new engine; uncomment to offer it again.
  // const showOverlayButton = document?.metadata?.is_scanned === true && document?.status === 'completed';
  // const overlayAvailable =
  //   document?.metadata?.translation_mode === 'overlay' && !document?.metadata?.overlay_degraded;
  // "pending" = overlay task still running (dispatched in parallel with the
  // translation). Distinct from degraded/unavailable so we don't claim the copy
  // failed while it's still being built - the user can re-open once it's ready.
  // const overlayPending = document?.metadata?.translation_mode === 'pending';

  if (loading) {
    return (
      <div className="chat-loading">
        <div className="spinner" />
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="chat-page">
      <header className="chat-header">
        <button className="btn btn-ghost header-back" onClick={() => navigate('/')}>
          <ArrowLeft size={18} />
          {t('header.back')}
        </button>
        <div className="header-title-block">
          <h1>{chat?.name || t('header.defaultName')}</h1>
          {document && document.language_from && document.language_from !== 'auto' && (
            <span className="header-lang-pair">
              {getLanguageDisplay(document.language_from)}
              {recognitionOnly ? (
                <span className="header-lang-arrow">· {t('header.recognitionOnly')}</span>
              ) : (
                <>
                  <span className="header-lang-arrow">→</span>
                  {getLanguageDisplay(document.language_to)}
                </>
              )}
            </span>
          )}
          {chat?.status !== 'auto_deleted' && (
            <Link to="/settings" className="header-retention-link" title={t('header.retentionTooltip')}>
              <Shield size={12} />
              {user?.auto_delete_days
                ? t('header.retentionActive', { days: user.auto_delete_days })
                : t('header.retentionNever')}
            </Link>
          )}
        </div>
      </header>

      <div className="chat-content">
        <div className="chat-main">
          {chat?.status === 'auto_deleted' ? (
            <div className="chat-auto-deleted-banner">
              <ShieldOff size={36} />
              <h2>{t('autoDeleted.title')}</h2>
              <p>
                {t('autoDeleted.body', {
                  date: chat?.auto_deleted_at
                    ? new Date(chat.auto_deleted_at).toLocaleDateString()
                    : '',
                })}
              </p>
              <button className="btn btn-primary" onClick={() => navigate('/')}>
                {t('autoDeleted.startNew')}
              </button>
            </div>
          ) : !document && !translating ? (
            <div className="upload-section">
              <h2>{t('upload.title')}</h2>
              <p>{t('upload.subtitle')}</p>
              <DocumentUpload onUpload={handleUpload} onUploadStart={handleUploadStart} disabled={translating} />
            </div>
          ) : (
            <>
              {translating && progress && (
                <TranslationProgress
                  currentStage={progress.stage}
                  iteration={progress.iteration}
                  maxIterations={progress.max_iterations}
                  message={progress.message}
                  pageDone={progress.page_done}
                  pageTotal={progress.page_total}
                  startTime={translationStartRef.current}
                  detectedLanguage={document?.language_from}
                />
              )}

              {/* Tabs + active panel share one flex column so the tab's
                  bottom border butts up against the chat/diff container
                  with no visible gap. */}
              <div className="tab-panel">
                {!recognitionOnly && (
                  <div className="view-tabs">
                    <button
                      className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
                      onClick={() => setActiveTab('chat')}
                    >
                      <FileText size={16} />
                      {t('tabs.translation')}
                    </button>
                    <button
                      className={`tab ${activeTab === 'diff' ? 'active' : ''}`}
                      onClick={loadDiff}
                    >
                      <Eye size={16} />
                      {t('tabs.compare')}
                      {showCompareNewBadge && hasInterestingDiff && (
                        <span className="feature-new-badge">NEW</span>
                      )}
                    </button>
                  </div>
                )}

                {activeTab === 'chat' ? (
                  <ChatInterface
                    messages={chat?.messages || []}
                    onSendMessage={handleSendMessage}
                    onRegenerate={handleRegenerate}
                    disabled={translating}
                    document={document}
                    translating={translating}
                    onCompareClick={recognitionOnly ? null : loadDiff}
                    lowConfidenceCount={lowConfidenceCount}
                    onDownload={handleDownload}
                    // showOverlayButton={showOverlayButton}
                    // overlayAvailable={overlayAvailable}
                    // overlayPending={overlayPending}
                    // onEditOverlay={() => setShowOverlayEditor(true)}
                  />
                ) : (
                  diffData && (
                    <DiffViewer
                      originalLines={diffData.original_lines}
                      translatedLines={diffData.translated_lines}
                      annotations={lastTranslation?.metadata?.annotations || []}
                      lineConfidence={lastTranslation?.metadata?.line_confidence || {}}
                      sourceLang={document?.language_from}
                      targetLang={document?.language_to}
                      documentId={document?.id}
                      handwriting={document?.metadata?.scan_handwriting || []}
                    />
                  )
                )}
              </div>
            </>
          )}
        </div>

        {hasAnalysis && !recognitionOnly && (
          <aside className="chat-sidebar">
            <PricePanel
              sourceChars={sourceChars}
              targetChars={targetChars}
              sourceWords={sourceWords}
              targetWords={targetWords}
              sourceLang={document?.language_from}
              targetLang={document?.language_to}
            />

            {lastTranslation?.metadata && (
              <ConfidenceScore
                score={lastTranslation.metadata.confidence_score || 0}
                iterations={lastTranslation.metadata.iteration}
                duration={translationDuration}
              />
            )}

            {lastTranslation?.metadata?.thoughts && (
              <div className="sidebar-card">
                <h3>{t('sidebar.analysis')}</h3>
                <p className="analysis-text">{lastTranslation.metadata.thoughts}</p>
              </div>
            )}

            {lastTranslation?.metadata?.plan?.length > 0 && (
              <div className="sidebar-card">
                <h3>{t('sidebar.translationPlan')}</h3>
                <ol className="plan-list">
                  {lastTranslation.metadata.plan.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              </div>
            )}
          </aside>
        )}
      </div>

      {reconnecting && (
        <div className="reconnecting-overlay">
          <div className="reconnecting-content">
            <div className="spinner" />
            <p>{t('reconnecting', 'Reconnecting...')}</p>
          </div>
        </div>
      )}

      <ErrorModal
        isVisible={errorModal.isVisible}
        onClose={() => setErrorModal({ isVisible: false, message: null, code: null })}
        errorMessage={errorModal.message}
        errorCode={errorModal.code}
        onRetry={document?.can_retry ? handleRetry : null}
      />

      <ScanSuggestionModal
        isVisible={!!scanSuggestion}
        reason={scanSuggestion}
        onClose={() => setScanSuggestion(null)}
      />

      <InDesignFontsDialog report={indesignReport} onClose={() => setIndesignReport(null)} />

      {/* 1:1 copy editor, off with the 1:1 copy:
      {document && (
        <OverlayEditor
          documentId={document.id}
          isOpen={showOverlayEditor}
          onClose={() => setShowOverlayEditor(false)}
          onSaved={async () => {
            await refreshDocument();
            handleDownload('overlay');
          }}
        />
      )} */}
    </div>
  );
}

export default ChatPage;
